import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Tag, ArrowRight, Eye } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { fetchPosts, fetchCategories } from '../services/newsApi';
import RotatingText from '../components/common/RotatingText';

const NewsPage = () => {
  const { isDarkMode } = useTheme();
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [postsData, categoriesData] = await Promise.all([
        fetchPosts({ status: 'published' }),
        fetchCategories()
      ]);
      setPosts(postsData);
      setCategories(categoriesData);
    } catch (error) {
      console.error('Error loading news:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredPosts = selectedCategory === 'all' 
    ? posts 
    : posts.filter(post => post.category_id === parseInt(selectedCategory));

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getCategoryInfo = (categoryId) => {
    return categories.find(c => c.id === categoryId);
  };

  if (loading) {
    return (
      <div className={`min-h-screen ${isDarkMode ? 'bg-fsgu-gray-900' : 'bg-fsgu-white'}`}>
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-fsgu-green-500 mb-4"></div>
            <p className={isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'}>
              Loading news...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-fsgu-gray-900' : 'bg-fsgu-white'}`}>
      {/* Hero Section - Matches HomePage */}
      <section className="relative min-h-[85vh] md:min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          {/* Mobile Image */}
          <img 
            src="/news-mobile.png" 
            alt="FSGU News" 
            className="md:hidden w-full h-full object-cover"
          />
          {/* Desktop Image */}
          <img 
            src="/news.png" 
            alt="FSGU News" 
            className="hidden md:block w-full h-full object-cover"
          />
          <div className={`absolute inset-0 ${
            isDarkMode 
              ? 'bg-gradient-to-b from-fsgu-gray-900/60 via-fsgu-gray-900/70 to-fsgu-gray-900/90' 
              : 'bg-gradient-to-b from-black/40 via-black/50 to-black/70'
          }`}></div>
        </div>

        {/* Content */}
        <div className="container-custom px-4 relative z-10 text-center">
          <div className="max-w-5xl mx-auto">
           <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold font-cursive mb-8 md:mb-12 text-white leading-tight tracking-tight">
  Union News
</h1>
            
            {/* Rotating Text Section */}
            <div className="flex flex-wrap items-center justify-center gap-3 text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-light mb-8">
              <span className="drop-shadow-lg text-white">Latest on</span>
              <RotatingText
                texts={[
                  'Tournaments',
                  'Results',
                  'Achievements',
                  'Events',
                  'Updates',
                ]}
                mainClassName={`font-cursive font-normal px-6 py-3 pb-4 rounded-2xl ${
                  isDarkMode
                    ? 'bg-fsgu-green-700/95 text-white'
                    : 'bg-fsgu-green-700 text-white'
                } drop-shadow-2xl`}
                splitLevelClassName="overflow-visible"
                staggerFrom="first"
                initial={{ y: '100%', opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: '-120%', opacity: 0 }}
                staggerDuration={0.03}
                transition={{ type: 'spring', damping: 30, stiffness: 400 }}
                rotationInterval={3000}
              />
            </div>

            <p className="text-lg sm:text-xl md:text-2xl text-white/90 font-light max-w-3xl mx-auto leading-relaxed">
              Stay updated with Free State Golf Union
            </p>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-12 left-1/2 transform -translate-x-1/2 z-10 hidden md:block">
          <div className="animate-bounce">
            <svg
              className="w-6 h-6 text-white"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path d="M19 14l-7 7m0 0l-7-7m7 7V3"></path>
            </svg>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="section py-6 md:py-8">
        <div className="container-custom px-4">
          <div className="flex flex-wrap gap-2 md:gap-3">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`px-4 md:px-6 py-2 text-sm md:text-base rounded-full font-medium transition-all ${
                selectedCategory === 'all'
                  ? 'bg-fsgu-green-500 text-white'
                  : isDarkMode
                  ? 'bg-fsgu-gray-800 text-fsgu-silver-300 hover:bg-fsgu-gray-700'
                  : 'bg-fsgu-silver-100 text-fsgu-silver-700 hover:bg-fsgu-silver-200'
              }`}
            >
              All News
            </button>
            {categories.map(category => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-4 md:px-6 py-2 text-sm md:text-base rounded-full font-medium transition-all ${
                  selectedCategory === category.id
                    ? 'text-white'
                    : isDarkMode
                    ? 'bg-fsgu-gray-800 text-fsgu-silver-300 hover:bg-fsgu-gray-700'
                    : 'bg-fsgu-silver-100 text-fsgu-silver-700 hover:bg-fsgu-silver-200'
                }`}
                style={selectedCategory === category.id ? { backgroundColor: category.color } : {}}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* News Grid */}
      <section className="section py-8 md:py-12">
        <div className="container-custom px-4">
          {filteredPosts.length === 0 ? (
            <div className="text-center py-12 md:py-20">
              <h3 className={`text-xl md:text-2xl font-semibold mb-4 ${
                isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
              }`}>
                No articles yet
              </h3>
              <p className={isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-600'}>
                Check back soon for updates!
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {filteredPosts.map(post => {
                const category = getCategoryInfo(post.category_id);
                return (
                  <article
                    key={post.id}
                    className={`rounded-xl overflow-hidden transition-all hover:shadow-xl ${
                      isDarkMode 
                        ? 'bg-fsgu-gray-800 hover:bg-fsgu-gray-750' 
                        : 'bg-white hover:shadow-2xl'
                    }`}
                  >
                    {/* Featured Image */}
                    {post.featured_image && (
                      <div className="aspect-video overflow-hidden">
                        <img
                          src={post.featured_image}
                          alt={post.title}
                          className="w-full h-full object-cover transition-transform hover:scale-105"
                        />
                      </div>
                    )}

                    {/* Content */}
                    <div className="p-6">
                      {/* Category Badge */}
                      {category && (
                        <div className="mb-3">
                          <span 
                            className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium text-white"
                            style={{ backgroundColor: category.color }}
                          >
                            <Tag className="w-3 h-3 mr-1" />
                            {category.name}
                          </span>
                        </div>
                      )}

                      {/* Title */}
                      <h3 className={`text-xl font-bold mb-3 line-clamp-2 ${
                        isDarkMode ? 'text-fsgu-silver-100' : 'text-fsgu-silver-900'
                      }`}>
                        {post.title}
                      </h3>

                      {/* Excerpt */}
                      {post.excerpt && (
                        <p className={`text-sm mb-4 line-clamp-3 ${
                          isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
                        }`}>
                          {post.excerpt}
                        </p>
                      )}

                      {/* Meta */}
                      <div className={`flex items-center gap-4 text-xs mb-4 ${
                        isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-500'
                      }`}>
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(post.published_at)}
                        </div>
                        <div className="flex items-center gap-1">
                          <Eye className="w-3 h-3" />
                          {post.views || 0}
                        </div>
                      </div>

                      {/* Read More */}
                      <Link
                        to={`/news/${post.slug}`}
                        className={`inline-flex items-center text-sm font-medium transition-colors ${
                          isDarkMode
                            ? 'text-fsgu-green-400 hover:text-fsgu-green-300'
                            : 'text-fsgu-green-600 hover:text-fsgu-green-700'
                        }`}
                      >
                        Read More
                        <ArrowRight className="w-4 h-4 ml-1" />
                      </Link>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default NewsPage;